import {
  type LucideIcon,
  Award,
  CheckCircle2,
  Flame,
  MessageSquarePlus,
  Sparkles,
  Trophy,
} from "lucide-react";

import { Badge } from "#app/components/ui/badge.tsx";
import { cn } from "#app/utils/misc.tsx";

type CommunityBadgeLike = {
  name?: string | null;
  rarity?: string | null;
  icon?: string | null;
};

const COMMUNITY_BADGE_ICON_MAP: Record<string, LucideIcon> = {
  "message-square": MessageSquarePlus,
  flame: Flame,
  "check-circle": CheckCircle2,
  trophy: Trophy,
  sparkles: Sparkles,
};

function getCommunityBadgeTone(badge: CommunityBadgeLike) {
  switch (badge.name) {
    case "First Post":
      return {
        pill: "border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300",
        iconWrap:
          "bg-sky-100 dark:bg-sky-950/40 text-sky-600 dark:text-sky-300",
      };
    case "Conversation Starter":
      return {
        pill: "border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300",
        iconWrap:
          "bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-300",
      };
    case "Helpful Solver":
      return {
        pill: "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300",
        iconWrap:
          "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300",
      };
    case "Top Contributor":
      return {
        pill: "border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300",
        iconWrap:
          "bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-300",
      };
    case "Challenge Participant":
      return {
        pill: "border-fuchsia-200 dark:border-fuchsia-700 bg-fuchsia-50 dark:bg-fuchsia-950/50 text-fuchsia-700 dark:text-fuchsia-200",
        iconWrap:
          "bg-fuchsia-100 dark:bg-fuchsia-950/50 text-fuchsia-600 dark:text-fuchsia-300",
      };
    default:
      switch (badge.rarity) {
        case "epic":
          return {
            pill: "border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300",
            iconWrap:
              "bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-300",
          };
        case "rare":
          return {
            pill: "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300",
            iconWrap:
              "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300",
          };
        case "uncommon":
          return {
            pill: "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300",
            iconWrap:
              "bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-300",
          };
        default:
          return {
            pill: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
            iconWrap:
              "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
          };
      }
  }
}

export function CommunityBadgeIcon({
  icon,
  className,
}: {
  icon?: string | null;
  className?: string;
}) {
  const Icon = icon ? COMMUNITY_BADGE_ICON_MAP[icon] : undefined;
  const ResolvedIcon = Icon ?? Award;
  return <ResolvedIcon className={className} />;
}

export function getCommunityBadgeStyles(badge: CommunityBadgeLike) {
  return getCommunityBadgeTone(badge);
}

export function CommunityBadgePill({
  badge,
  className,
}: {
  badge: CommunityBadgeLike;
  className?: string;
}) {
  const styles = getCommunityBadgeTone(badge);

  return (
    <Badge
      variant="outline"
      className={cn("gap-1 border font-normal", styles.pill, className)}
    >
      <CommunityBadgeIcon icon={badge.icon} className="h-3 w-3" />
      <span>{badge.name || "Badge"}</span>
    </Badge>
  );
}
